import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MapPin, Loader2 } from "lucide-react";

export default function LoginPage() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { loginEmail, signupEmail } = useAuth();
  const [tab, setTab] = useState(sp.get("mode") === "signup" ? "signup" : "login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = tab === "login"
        ? await loginEmail(form.email, form.password)
        : await signupEmail(form.email, form.password, form.name || form.email.split("@")[0]);
      toast.success(`Welcome, ${user.name}`);
      nav(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] gradient-emerald-hero flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-800 text-white flex items-center justify-center"><MapPin className="w-5 h-5" /></div>
            <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Apna<span className="text-emerald-700">Islamabad</span></span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Welcome to your local marketplace</h1>
          <p className="text-slate-600 text-sm mt-1">Log in to write reviews, save favorites, and manage your listings.</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-6">
          <Button onClick={googleLogin} variant="outline" className="w-full h-11 font-semibold border-slate-300 mb-4" data-testid="google-login-btn">
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </Button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-slate-400">or</span></div>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="login" data-testid="tab-login">Log in</TabsTrigger>
              <TabsTrigger value="signup" data-testid="tab-signup">Sign up</TabsTrigger>
            </TabsList>
            <form onSubmit={submit}>
              <TabsContent value="signup" className="space-y-3 mt-4">
                <div>
                  <Label>Full name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="signup-name-input" />
                </div>
              </TabsContent>
              <div className="space-y-3 mt-4">
                <div>
                  <Label>Email</Label>
                  <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid={`${tab}-email-input`} />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" data-testid={`${tab}-password-input`} />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-11 bg-emerald-800 hover:bg-emerald-900" data-testid={`${tab}-submit-btn`}>
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {tab === "login" ? "Log in" : "Create account"}
                </Button>
              </div>
            </form>
          </Tabs>
        </div>

        <div className="text-center mt-4 text-xs text-slate-500">
          By continuing you agree to our <Link to="/" className="text-emerald-700 hover:underline">Terms</Link>.
        </div>
      </div>
    </div>
  );
}
